import React from "react"
import { ReactComponent as EmergencyFundIcon } from "uikit/Icons/emergencyfund-goal.svg"
import { ReactComponent as HomeIcon } from "uikit/Icons/homepurchase-goal.svg"
import { ReactComponent as RetirementIcon } from "uikit/Icons/retirement-goal.svg"
import { ReactComponent as CollegeIcon } from "uikit/Icons/college-goal.svg"
import { ReactComponent as ProtectIcon } from "uikit/Icons/protect-goal.svg"
import { ReactComponent as DebtPayoffIcon } from "uikit/Icons/debtpayoff-goal.svg"
import { ReactComponent as OtherGoalIcon } from "uikit/Icons/other-goal.svg"
import { ReactComponent as CharityGoalIcon } from "uikit/Icons/charity-goal.svg"
import { ReactComponent as CaregivingIcon } from "uikit/Icons/caregiving-goal.svg"
import { ReactComponent as CarGoalIcon } from "uikit/Icons/car-goal.svg"
import { ReactComponent as StudentLoanPayOffIcon } from "uikit/Icons/studentloanpayoff-goal.svg"
import { ReactComponent as WorkPauseIcon } from "uikit/Icons/workpause-goal.svg"
import Methodology from "App/Methodology"

export const goalTypes = {
  emergencyfund: {
    label: "Emergency Fund",
    worksheetType: "EmergencyFund",
    cardType: "EmergencyFund",
    showFirst: () => true,
    description: (
      <span>
        An emergency fund is money you've saved that could be used to cover
        unexpected expenses or maintain your spending while in between jobs
        without sacrificing your financial goals.
      </span>
    ),
    category: "emergencyfund",
    singular: true,
    insurable: true,
    icon: EmergencyFundIcon
  },
  homepurchase: {
    label: "Home Purchase",
    worksheetType: "HomePurchase",
    cardType: "HomePurchase",
    showFirst: ({ household }) => household.is_renter,
    description: (
      <span>
        Buying a house can be as stressful as it is exciting. We can help you
        decide how much house is right for you and and exactly how to pay for
        it&mdash; from tax-smart strategies for the down payment to selecting
        the right mortgage term for you.
      </span>
    ),
    category: "homepurchase",
    singular: false,
    insurable: true,
    icon: HomeIcon
  },
  retirement: {
    label: "Retirement",
    worksheetType: "Retirement",
    cardType: "Retirement",
    showFirst: () => true,
    description: (
      <>
        <span>
          In spite of the name, retirement isn't about stopping, but instead, it
          is to establish a different balance in life with the financial
          independence you've achieved through years of hard work. We can help
          you find the right time to retire and craft a tax-smart strategy to
          prepare for it and sustain a confident retirement.
        </span>
        <br />
        <br />
        <span>
          The SAVVI{" "}
          <span className="navlink">
            <Methodology LinkTitle="methodology" />
          </span>{" "}
          outlines the criteria and methods behind the Retirement goal,
          including key assumptions and limitations of the tool.
        </span>
      </>
    ),
    category: "retirement",
    singular: true,
    insurable: false,
    icon: RetirementIcon
  },
  college: {
    label: "College",
    worksheetType: "College",
    cardType: "College",
    showFirst: ({ people }) =>
      people.filter(({ role }) => role === "child").length >= 1,
    description: (
      <span>
        You want to give your kid the best start in life you can, but the
        thought of saving for college is overwhelming. We can help you plan for
        future college expenses and planning ahead will make them less of a
        burden!
      </span>
    ),
    category: "college",
    singular: false,
    insurable: true,
    icon: CollegeIcon
  },
  lifeinsurance: {
    label: "Life Insurance",
    worksheetType: "LifeInsurance",
    cardType: "LifeInsurance",
    showFirst: ({ people }) =>
      people.filter(({ role }) => role === "child").length >= 1,
    description: (
      <span>
        Planning for unexpected death can be unsettling, but your family depends
        on you so protecting their future is an important part of a solid
        financial plan. As painlessly as possible, we'll analyze your needs and
        resources to propose a term life insurance policy for you.
      </span>
    ),
    category: "lifeinsurance",
    singular: true,
    insurable: false,
    icon: ProtectIcon
  },
  debtpayoff: {
    label: "Debt Payoff",
    worksheetType: "DebtPayoff",
    cardType: "DebtPayoff",
    showFirst: () => false,
    description: (
      <span>
        It looks like you're carrying a credit card balance. Let's put a plan in
        place to free you from that burden by paying it off. Simply select the
        credit cards you want to payoff and we'll put an optimized payoff plan
        together that balances paying off your debt with your other goals.
      </span>
    ),
    category: "debtpayoff",
    singular: true,
    insurable: false,
    icon: DebtPayoffIcon
  },
  privateschool: {
    label: "Private School",
    worksheetType: "PrivateSchool",
    cardType: "PrivateSchool",
    showFirst: () => false,
    description: (
      <span>
        For many families, a private primary or high school education is an
        important foundation for lifetime success and recent tax code changes
        create even more opportunities for tax efficient planning strategies.
      </span>
    ),
    category: "privateschool",
    singular: false,
    insurable: true,
    icon: CollegeIcon
  },
  other: {
    label: "Other",
    worksheetType: "Other",
    cardType: "Other",
    description: (
      <span>You can plan for many other goals like vacation or a wedding.</span>
    ),
    category: "other",
    singular: false,
    insurable: true,
    icon: OtherGoalIcon
  },
  car: {
    label: "Car",
    worksheetType: "Car",
    cardType: "Car",
    description: (
      <span>
        Buying a car can be as stressful as it is exciting. We can help you
        decide what you can afford and how to pay for it. We can also help you
        plan for the costs of periodically replacing your car.
      </span>
    ),
    category: "car",
    singular: false,
    insurable: true,
    icon: CarGoalIcon
  },
  charity: {
    label: "Charity",
    worksheetType: "Charity",
    cardType: "Charity",
    description: <span>You can plan for many charity goals.</span>,
    category: "charity",
    singular: false,
    insurable: true,
    icon: CharityGoalIcon
  },
  caregiving: {
    label: "Caregiving",
    worksheetType: "Caregiving",
    cardType: "Caregiving",
    description: (
      <span>
        Plan for the financial resources you need to help care for loved ones.
        Caregiving is hard work and can be expensive as you carry the cost of
        things like medical equipment, specialized furniture, and support
        services.
      </span>
    ),
    category: "caregiving",
    singular: false,
    insurable: true,
    icon: CaregivingIcon
  },
  studentloanpayoff: {
    label: "Student Loan Payoff",
    worksheetType: "StudentLoanPayoff",
    cardType: "StudentLoanPayoff",
    showFirst: () => true,
    description: (
      <span>
        What’s the best plan for paying down your student loans? How do you
        balance student loan payments with your other goals? The Student Loan
        Payoff goal will create an optimized payment schedule within the context
        of your complete financial picture and evaluate the benefits of changing
        your repayment plan or refinancing.
      </span>
    ),
    category: "debtpayoff",
    singular: true,
    insurable: false,
    icon: StudentLoanPayOffIcon
  },
  workpause: {
    label: "Weather the Storm",
    worksheetType: "WorkPause",
    cardType: "WorkPause",
    description: (
      <span>
        Plan through a period of reduced income due to the coronavirus using the
        relief opportunities provided to individuals by the{" "}
        <a
          href="https://www.congress.gov/bill/116th-congress/senate-bill/3548/text#toc-id6DE96266C1C54006B3C1D6A99C6EC21C"
          target="_blank"
          rel="noopener noreferrer"
        >
          CARES Act
        </a>
        , such as the special rules for use of retirement funds.
      </span>
    ),
    category: "workpause",
    singular: false,
    insurable: false,
    icon: WorkPauseIcon
  }
}
